import { createAction } from '@reduxjs/toolkit';
import { type LatLng } from '~/_shared/types/latLng';
import { createUuid } from '~/_shared/utils/createUuid';
import { type BoundingBox } from '~/map/map/boundingBox';
import {
  ALLOW_STREET_VIEW,
  DISALLOW_STREET_VIEW,
  MAP_COMPONENT_SET_CENTER, MAP_COMPONENT_SET_CENTER_AND_ZOOM, MAP_COMPONENT_SET_LAST_BOUNDS,
  MAP_COMPONENT_SET_ZOOM, MAP_COMPONENT_SET_ZOOM_TO_BOUNDS,
} from './mapComponent.actionTypes';
import { type LastBounds } from './mapComponent.state';

// Each of the 4 map-authoring setters mints a fresh UUID when `resetMap` is
// true; that UUID becomes the action's op id. The reducer stores the op id in
// `authoringOps` and effects in `useMapFeatures` fire once per new id. When
// `resetMap` is false (sync dispatches from the map's own idle listener) the
// op id is `null` so no author is scheduled and no pending author is disturbed.

export const mapComponentSetCenter = (center: LatLng, resetMap = true) => ({
  type: MAP_COMPONENT_SET_CENTER,
  payload: {
    center,
    centerOpId: resetMap ? createUuid() : null,
  },
}) as const;

export const mapComponentSetZoom = (zoom: number | null, resetMap = true) => ({
  type: MAP_COMPONENT_SET_ZOOM,
  payload: {
    zoom,
    zoomOpId: resetMap ? createUuid() : null,
  },
}) as const;

export const mapComponentSetCenterAndZoom = (center: LatLng, zoom: number | null, resetMap = true) => ({
  type: MAP_COMPONENT_SET_CENTER_AND_ZOOM,
  payload: {
    center,
    zoom,
    centerOpId: resetMap ? createUuid() : null,
    zoomOpId: resetMap ? createUuid() : null,
  },
}) as const;

export const mapComponentSetZoomToBounds = (boundingBox: BoundingBox, options?: { resetMap?: boolean; preferredZoom?: number }) => ({
  type: MAP_COMPONENT_SET_ZOOM_TO_BOUNDS,
  payload: {
    boundingBox,
    preferredZoom: options?.preferredZoom,
    boundsOpId: (options?.resetMap ?? true) ? createUuid() : null,
  },
}) as const;

export const mapComponentSetLastBounds = (boundingBox: BoundingBox, zoomLevel: number) => ({
  type: MAP_COMPONENT_SET_LAST_BOUNDS,
  payload: {
    boundingBox,
    zoomLevel,
  },
}) as const;

export const mapComponentLastBoundsChanged = createAction('MAP_COMPONENT_LAST_BOUNDS_CHANGED', ({ previousBounds, newBounds }: {
  previousBounds: LastBounds | null;
  newBounds: LastBounds | null;
}) => ({
  payload: {
    previousBounds,
    newBounds,
  },
}));

export const mapComponentDisallowStreetView = () => ({
  type: DISALLOW_STREET_VIEW,
}) as const;

export const mapComponentAllowStreetView = () => ({
  type: ALLOW_STREET_VIEW,
}) as const;
